/**
 * Admin Posted-Kit Settings page — /admin/settings/posted-kit
 *
 * Single home for the customer-facing posted-kit (A144) knobs:
 *   • Kit example image (upload OR pasted URL) + caption
 *   • Kit fee in AUD (stored as cents)
 *   • Lab pickup address
 *
 * Reads + writes hit /api/admin/platform-settings; the Booking page
 * reads the values fresh via /api/public/posted-kit-settings on every
 * mount so a mid-day tweak here goes live without a redeploy.
 *
 * The `posted_kit_paid` feature flag is exposed at the top of this
 * page as a "Public visibility" card (Off / Internal preview / Public).
 * The same three-state control is also mirrored on the general
 * Feature Flags admin page; both surfaces write to the same flag.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Upload,
  Link as LinkIcon,
  ImageOff,
  Loader2,
  Save,
  RefreshCw,
  Info,
  Trash2,
  Package,
  Eye,
  EyeOff,
  Users,
} from "lucide-react";
import { PortalShell } from "@/components/PortalShell";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { refreshFeatureFlags } from "@/lib/useFeature";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const ALLOWED_MIME = ["image/jpeg", "image/png", "image/webp"];

function resolveImageSrc(url) {
  if (!url) return null;
  const s = String(url).trim();
  if (!s) return null;
  // Data URLs (Feb 2026 durability fix) and absolute http(s) URLs
  // pass through unchanged; only legacy relative /api/uploads/…
  // paths need the backend host prefix.
  if (s.startsWith("data:")) return s;
  if (/^https?:\/\//i.test(s)) return s;
  if (s.startsWith("/")) return `${BACKEND_URL}${s}`;
  return s;
}

function centsToDollars(cents) {
  if (cents === null || cents === undefined || cents === "") return "";
  const n = Number(cents);
  if (!Number.isFinite(n)) return "";
  return (n / 100).toFixed(2);
}

function dollarsToCents(dollars) {
  const n = Number(dollars);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

export default function AdminPostedKitSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [initial, setInitial] = useState(null);
  const [form, setForm] = useState({
    posted_kit_image_url: "",
    posted_kit_image_caption: "",
    posted_kit_fee_dollars: "",
    posted_kit_lab_pickup_address: "",
    posted_kit_weight_kg: "",
    posted_kit_length_cm: "",
    posted_kit_width_cm: "",
    posted_kit_height_cm: "",
  });
  const fileInputRef = useRef(null);

  // Feb 2026 — dedicated public-visibility switch for the posted-kit
  // picker on /book. Backed by the shared `posted_kit_paid` feature
  // flag so the FeatureFlags admin page + this page stay in sync.
  const [flagStatus, setFlagStatus] = useState(null);
  const [flagBusy, setFlagBusy] = useState(false);

  const loadFlag = async () => {
    try {
      const { data } = await api.get("/admin/feature-flags");
      const flags = data?.flags || data || [];
      const f = (Array.isArray(flags) ? flags : []).find((x) => x.key === "posted_kit_paid");
      setFlagStatus(f?.status || "disabled");
    } catch (e) {
      // Don't lie to the admin about the real state — keep the card
      // in its skeleton/loading placeholder and surface an error the
      // admin can react to (retry via the Feature Flags page).
      setFlagStatus(null);
      toast.error(
        e.response?.data?.detail
          || "Could not load current visibility. Please refresh."
      );
    }
  };

  const setFlag = async (next) => {
    if (next === flagStatus) return;
    setFlagBusy(true);
    try {
      await api.put(`/admin/feature-flags/posted_kit_paid`, {
        status: next,
        reason: "posted-kit settings page toggle",
        override_dependency: false,
      });
      setFlagStatus(next);
      await refreshFeatureFlags();
      toast.success(
        next === "enabled" ? "Posted-kit picker is now LIVE for the public."
        : next === "internal_only" ? "Posted-kit picker is hidden from the public — visible only to admin/lab preview."
        : "Posted-kit picker is now HIDDEN from everyone (including admin)."
      );
    } catch (e) {
      const detail = e.response?.data?.detail || "Update failed";
      if (typeof detail === "string" && detail.startsWith("dependency_disabled:")) {
        const dep = detail.split(":")[1];
        if (window.confirm(`This depends on "${dep}" which is disabled. Enable anyway?`)) {
          try {
            await api.put(`/admin/feature-flags/posted_kit_paid`, {
              status: next, reason: "posted-kit page toggle (dep override)", override_dependency: true,
            });
            setFlagStatus(next);
            await refreshFeatureFlags();
            toast.success("Updated with dependency override.");
          } catch (e2) {
            toast.error(e2.response?.data?.detail || "Update failed");
          }
        }
      } else {
        toast.error(detail);
      }
    } finally {
      setFlagBusy(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/platform-settings");
      const seeded = {
        posted_kit_image_url: data.posted_kit_image_url || "",
        posted_kit_image_caption: data.posted_kit_image_caption || "",
        posted_kit_fee_dollars: centsToDollars(data.posted_kit_fee_cents),
        posted_kit_lab_pickup_address: data.posted_kit_lab_pickup_address || "",
        posted_kit_weight_kg:  data.posted_kit_weight_kg != null ? String(data.posted_kit_weight_kg) : "",
        posted_kit_length_cm:  data.posted_kit_length_cm != null ? String(data.posted_kit_length_cm) : "",
        posted_kit_width_cm:   data.posted_kit_width_cm  != null ? String(data.posted_kit_width_cm)  : "",
        posted_kit_height_cm:  data.posted_kit_height_cm != null ? String(data.posted_kit_height_cm) : "",
      };
      setInitial(seeded);
      setForm(seeded);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load posted-kit settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Aware Admin · Posted Kit Settings";
    load();
    loadFlag();
  }, []);

  const dirty = useMemo(() => {
    if (!initial) return false;
    return (
      form.posted_kit_image_url !== initial.posted_kit_image_url ||
      form.posted_kit_image_caption !== initial.posted_kit_image_caption ||
      form.posted_kit_fee_dollars !== initial.posted_kit_fee_dollars ||
      form.posted_kit_lab_pickup_address !== initial.posted_kit_lab_pickup_address ||
      form.posted_kit_weight_kg !== initial.posted_kit_weight_kg ||
      form.posted_kit_length_cm !== initial.posted_kit_length_cm ||
      form.posted_kit_width_cm !== initial.posted_kit_width_cm ||
      form.posted_kit_height_cm !== initial.posted_kit_height_cm
    );
  }, [form, initial]);

  const previewSrc = resolveImageSrc(form.posted_kit_image_url);

  const handleFilePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED_MIME.includes(file.type)) {
      toast.error("Please choose a JPEG, PNG or WebP image.");
      e.target.value = "";
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error(`Image is ${(file.size / (1024 * 1024)).toFixed(1)} MB — the limit is 5 MB.`);
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        "/admin/platform-settings/posted-kit-image",
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const newUrl = data.posted_kit_image_url || "";
      setForm((f) => ({ ...f, posted_kit_image_url: newUrl }));
      setInitial((prev) => (prev ? { ...prev, posted_kit_image_url: newUrl } : prev));
      toast.success("Kit image uploaded — customers will see it on their next visit to the booking page.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed. Try a JPEG / PNG / WebP under 5 MB.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleClearImage = async () => {
    if (!window.confirm("Remove the current kit image? The booking page will fall back to the placeholder card.")) {
      return;
    }
    try {
      const { data } = await api.delete("/admin/platform-settings/posted-kit-image");
      const newUrl = data.posted_kit_image_url || "";
      setForm((f) => ({ ...f, posted_kit_image_url: newUrl }));
      setInitial((prev) => (prev ? { ...prev, posted_kit_image_url: newUrl } : prev));
      toast.success("Kit image cleared.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not clear image.");
    }
  };

  const handleSave = async () => {
    const cents = dollarsToCents(form.posted_kit_fee_dollars);
    if (cents === null) {
      toast.error("Kit fee must be a positive number (e.g. 49 or 49.00).");
      return;
    }
    if (cents > 100_000) {
      toast.error("Kit fee is capped at $1000. Enter a smaller amount.");
      return;
    }
    setSaving(true);
    try {
      // Parse parcel dimensions with sane rejection for typos.
      const dims = {
        posted_kit_weight_kg:  parseFloat(form.posted_kit_weight_kg),
        posted_kit_length_cm:  parseFloat(form.posted_kit_length_cm),
        posted_kit_width_cm:   parseFloat(form.posted_kit_width_cm),
        posted_kit_height_cm:  parseFloat(form.posted_kit_height_cm),
      };
      for (const [k, v] of Object.entries(dims)) {
        if (!Number.isFinite(v) || v <= 0) {
          toast.error(`Enter a positive number for ${k.replace(/^posted_kit_/, "").replace(/_/g, " ")}.`);
          setSaving(false);
          return;
        }
      }
      const payload = {
        posted_kit_image_url: form.posted_kit_image_url,
        posted_kit_image_caption: form.posted_kit_image_caption,
        posted_kit_fee_cents: cents,
        posted_kit_lab_pickup_address: form.posted_kit_lab_pickup_address,
        ...dims,
      };
      const { data } = await api.patch("/admin/platform-settings", payload);
      const seeded = {
        posted_kit_image_url: data.posted_kit_image_url || "",
        posted_kit_image_caption: data.posted_kit_image_caption || "",
        posted_kit_fee_dollars: centsToDollars(data.posted_kit_fee_cents),
        posted_kit_lab_pickup_address: data.posted_kit_lab_pickup_address || "",
        posted_kit_weight_kg:  data.posted_kit_weight_kg != null ? String(data.posted_kit_weight_kg) : "",
        posted_kit_length_cm:  data.posted_kit_length_cm != null ? String(data.posted_kit_length_cm) : "",
        posted_kit_width_cm:   data.posted_kit_width_cm  != null ? String(data.posted_kit_width_cm)  : "",
        posted_kit_height_cm:  data.posted_kit_height_cm != null ? String(data.posted_kit_height_cm) : "",
      };
      setInitial(seeded);
      setForm(seeded);
      toast.success("Saved. Customers will see the change on their next booking-page visit.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg || String(d)).join(" · ")
        : (detail || "Save failed.");
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <PortalShell>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        <div className="mb-7">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-700">
            Admin · Settings
          </div>
          <h1
            data-testid="posted-kit-settings-heading"
            className="font-display text-3xl sm:text-4xl font-semibold text-slate-900 mt-2 flex items-center gap-3"
          >
            <Package className="w-8 h-8 text-cyan-600" />
            Posted Kit Settings
          </h1>
          <p className="text-sm text-slate-500 mt-2 max-w-2xl">
            Customer-facing content shown on the booking page when a customer picks
            &ldquo;Post a kit to me with a prepaid return label.&rdquo; Changes save immediately —
            the booking page reads these values fresh on every visit, so a redeploy
            isn&rsquo;t needed.
          </p>
        </div>

        {/* Public visibility switch — the master on/off for the
            posted-kit picker on the public /book page. Backed by the
            shared `posted_kit_paid` feature flag, so this card and
            the /admin/settings/features page stay in sync. */}
        <div
          data-testid="posted-kit-visibility-card"
          className="mb-6 rounded-2xl border border-slate-200 bg-white p-5 sm:p-6"
        >
          <div className="flex items-start gap-3 mb-3">
            <div className="w-9 h-9 rounded-xl bg-cyan-50 text-cyan-700 flex items-center justify-center flex-shrink-0">
              {flagStatus === "enabled" ? <Eye className="w-4 h-4" />
                : flagStatus === "internal_only" ? <Users className="w-4 h-4" />
                : <EyeOff className="w-4 h-4" />}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-semibold text-slate-900">Public visibility</h2>
              <p className="text-[13px] text-slate-500 mt-0.5">
                Master on/off for the &ldquo;How would you like to receive your sample kit?&rdquo;
                picker on the public booking page. Also mirrored on{" "}
                <a href="/admin/settings/features" className="underline hover:text-slate-800">
                  Feature Flags
                </a>.
              </p>
            </div>
          </div>

          {flagStatus === null ? (
            <div className="text-[13px] text-slate-500 flex items-center gap-2 py-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading current status…
            </div>
          ) : (
            <>
              <div
                data-testid="posted-kit-visibility-switch"
                className="grid sm:grid-cols-3 gap-2.5"
              >
                {[
                  {
                    key: "disabled",
                    icon: EyeOff,
                    label: "Off",
                    hint: "Hidden from everyone (including admins).",
                    tone: "slate",
                  },
                  {
                    key: "internal_only",
                    icon: Users,
                    label: "Internal preview",
                    hint: "Visible only to admin & lab_technician logins. Public users see nothing.",
                    tone: "amber",
                  },
                  {
                    key: "enabled",
                    icon: Eye,
                    label: "Public",
                    hint: "Visible to every customer on /book.",
                    tone: "emerald",
                  },
                ].map((opt) => {
                  const active = flagStatus === opt.key;
                  const Icon = opt.icon;
                  const toneRing = {
                    slate: "border-slate-500 bg-slate-50 ring-2 ring-slate-400/30",
                    amber: "border-amber-500 bg-amber-50 ring-2 ring-amber-400/30",
                    emerald: "border-emerald-500 bg-emerald-50 ring-2 ring-emerald-400/30",
                  }[opt.tone];
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      disabled={flagBusy}
                      data-testid={`posted-kit-visibility-${opt.key}`}
                      onClick={() => setFlag(opt.key)}
                      className={`text-left rounded-xl border p-3 transition-all ${
                        active ? toneRing : "border-slate-200 bg-white hover:border-slate-300"
                      } disabled:opacity-60 disabled:cursor-not-allowed`}
                    >
                      <div className="flex items-center gap-2">
                        <Icon className={`w-4 h-4 ${
                          active
                            ? { slate: "text-slate-700", amber: "text-amber-700", emerald: "text-emerald-700" }[opt.tone]
                            : "text-slate-400"
                        }`} />
                        <div className="font-semibold text-[13px] text-slate-900">{opt.label}</div>
                        {active && <span className="ml-auto text-[10px] font-bold uppercase tracking-wider text-slate-500">Active</span>}
                      </div>
                      <div className="mt-1.5 text-[11px] text-slate-500 leading-snug">{opt.hint}</div>
                    </button>
                  );
                })}
              </div>
              <div className="mt-3 text-[11px] text-slate-500 flex items-center gap-1.5">
                <Info className="w-3 h-3" />
                Change takes effect on the next booking-page visit — no redeploy needed.
              </div>
            </>
          )}
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-slate-500 py-10">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading current settings…
          </div>
        ) : (
          <div className="space-y-6">
            {/* ── Kit image ────────────────────────────────────────── */}
            <section
              data-testid="posted-kit-image-section"
              className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6"
            >
              <div className="flex items-center gap-2 mb-1">
                <h2 className="text-lg font-semibold text-slate-900">Kit example image</h2>
              </div>
              <p className="text-[13px] text-slate-500 mb-4">
                Shown next to the &ldquo;Post a kit to me&rdquo; option. Upload a photo from
                your Mac / phone or paste a public image URL. JPEG / PNG / WebP,
                5 MB max.
              </p>

              {/* Preview panel */}
              <div className="grid sm:grid-cols-[minmax(0,240px),1fr] gap-4 items-start">
                <div
                  data-testid="posted-kit-image-preview"
                  className="w-full h-40 rounded-lg overflow-hidden bg-slate-50 border border-slate-200 flex items-center justify-center"
                >
                  {previewSrc ? (
                    <img
                      src={previewSrc}
                      alt="Kit preview"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  ) : (
                    <div className="text-center px-3 text-slate-400">
                      <ImageOff className="w-8 h-8 mx-auto mb-2" />
                      <div className="text-[11px] uppercase tracking-wider font-bold">No image set</div>
                      <div className="text-[11px] mt-1">Booking page falls back to placeholder card</div>
                    </div>
                  )}
                </div>

                <div className="space-y-3">
                  {/* Upload */}
                  <div>
                    <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1.5">
                      <Upload className="w-3 h-3" /> Upload from your device
                    </label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={ALLOWED_MIME.join(",")}
                      onChange={handleFilePick}
                      disabled={uploading}
                      data-testid="posted-kit-image-file-input"
                      className="block w-full text-[13px] text-slate-700
                        file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0
                        file:text-[13px] file:font-semibold
                        file:bg-cyan-600 file:text-white
                        hover:file:bg-cyan-700
                        disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    {uploading && (
                      <div className="mt-2 flex items-center gap-1.5 text-[12px] text-slate-500">
                        <Loader2 className="w-3 h-3 animate-spin" /> Uploading…
                      </div>
                    )}
                  </div>

                  {/* URL */}
                  <div>
                    <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1.5">
                      <LinkIcon className="w-3 h-3" /> Or paste an image URL
                    </label>
                    <input
                      type="text"
                      value={form.posted_kit_image_url}
                      onChange={(e) => setForm((f) => ({ ...f, posted_kit_image_url: e.target.value }))}
                      placeholder="https://cdn.example.com/kit.jpg"
                      data-testid="posted-kit-image-url-input"
                      className="w-full px-3 py-2 rounded-lg border border-slate-300 text-[13px] focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400"
                    />
                    <p className="mt-1 text-[11px] text-slate-400">
                      Accepts an <code className="font-mono">https://</code> URL or a backend{" "}
                      <code className="font-mono">/api/uploads/…</code> path from the uploader.
                    </p>
                  </div>

                  {form.posted_kit_image_url && (
                    <div>
                      <button
                        type="button"
                        onClick={handleClearImage}
                        data-testid="posted-kit-image-clear-btn"
                        className="inline-flex items-center gap-1.5 text-[12px] font-medium text-rose-600 hover:text-rose-800"
                      >
                        <Trash2 className="w-3 h-3" /> Clear image
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Caption */}
              <div className="mt-5">
                <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">
                  Image caption
                </label>
                <textarea
                  rows={3}
                  value={form.posted_kit_image_caption}
                  onChange={(e) => setForm((f) => ({ ...f, posted_kit_image_caption: e.target.value }))}
                  placeholder="Your posted kit includes a sterile sample bottle, step-by-step instructions…"
                  data-testid="posted-kit-image-caption-input"
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-[13px] focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400"
                  maxLength={500}
                />
                <p className="mt-1 text-[11px] text-slate-400">
                  Appears just below the &ldquo;What you will receive&rdquo; heading on the booking page.
                  Leave blank to hide the caption.
                </p>
              </div>
            </section>

            {/* ── Fee ─────────────────────────────────────────────── */}
            <section
              data-testid="posted-kit-fee-section"
              className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6"
            >
              <h2 className="text-lg font-semibold text-slate-900 mb-1">Kit fee (AUD)</h2>
              <p className="text-[13px] text-slate-500 mb-4">
                Charged as a <em>separate</em> Stripe line item — never bundled into
                the test price. Stored in whole cents; enter a dollar amount here.
              </p>
              <div className="flex items-center gap-2 max-w-xs">
                <span className="text-slate-500 text-lg font-medium">$</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.posted_kit_fee_dollars}
                  onChange={(e) => setForm((f) => ({ ...f, posted_kit_fee_dollars: e.target.value }))}
                  data-testid="posted-kit-fee-input"
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-[15px] focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400"
                />
                <span className="text-slate-500 text-[13px]">AUD</span>
              </div>
              <p className="mt-2 text-[11px] text-slate-400">
                Set to <code className="font-mono">0</code> to offer the posted kit at no extra charge.
              </p>
            </section>

            {/* ── Lab pickup address ───────────────────────────────── */}
            <section
              data-testid="posted-kit-address-section"
              className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6"
            >
              <h2 className="text-lg font-semibold text-slate-900 mb-1">Lab pickup address</h2>
              <p className="text-[13px] text-slate-500 mb-4">
                Displayed to customers who pick &ldquo;I will collect a kit from the Aware lab.&rdquo;
                Also referenced in the customer confirmation email.
              </p>
              <textarea
                rows={2}
                value={form.posted_kit_lab_pickup_address}
                onChange={(e) => setForm((f) => ({ ...f, posted_kit_lab_pickup_address: e.target.value }))}
                placeholder="Aware Water Testing · Unit 1/37 Gateway Drive, Noosaville QLD 4566"
                data-testid="posted-kit-address-input"
                className="w-full px-3 py-2 rounded-lg border border-slate-300 text-[13px] focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400"
                maxLength={500}
              />
            </section>

            {/* ── Parcel dimensions (Starshipit) ─────────────────── */}
            <section
              data-testid="posted-kit-dimensions-section"
              className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6"
            >
              <h2 className="text-lg font-semibold text-slate-900 mb-1">Parcel dimensions</h2>
              <p className="text-[13px] text-slate-500 mb-4">
                Used by the Starshipit / Australia Post integration to build
                the shipping label. Weight in <strong>kilograms</strong>,
                dimensions in <strong>centimetres</strong>. Placeholder
                defaults are 0.3 kg / 22 × 16 × 5 cm — update these once you
                have the real packed kit measurements.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <label className="block">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Weight (kg)</span>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={form.posted_kit_weight_kg}
                    onChange={(e) => setForm((f) => ({ ...f, posted_kit_weight_kg: e.target.value }))}
                    data-testid="posted-kit-weight-input"
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-[14px] focus:outline-none focus:ring-2 focus:ring-cyan-400"
                  />
                </label>
                <label className="block">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Length (cm)</span>
                  <input
                    type="number"
                    min="0.1"
                    step="0.1"
                    value={form.posted_kit_length_cm}
                    onChange={(e) => setForm((f) => ({ ...f, posted_kit_length_cm: e.target.value }))}
                    data-testid="posted-kit-length-input"
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-[14px] focus:outline-none focus:ring-2 focus:ring-cyan-400"
                  />
                </label>
                <label className="block">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Width (cm)</span>
                  <input
                    type="number"
                    min="0.1"
                    step="0.1"
                    value={form.posted_kit_width_cm}
                    onChange={(e) => setForm((f) => ({ ...f, posted_kit_width_cm: e.target.value }))}
                    data-testid="posted-kit-width-input"
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-[14px] focus:outline-none focus:ring-2 focus:ring-cyan-400"
                  />
                </label>
                <label className="block">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Height (cm)</span>
                  <input
                    type="number"
                    min="0.1"
                    step="0.1"
                    value={form.posted_kit_height_cm}
                    onChange={(e) => setForm((f) => ({ ...f, posted_kit_height_cm: e.target.value }))}
                    data-testid="posted-kit-height-input"
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-[14px] focus:outline-none focus:ring-2 focus:ring-cyan-400"
                  />
                </label>
              </div>
            </section>

            {/* Feb 2026 — Backfill recovery card. Fixes all-inclusive
                mail-out bookings that were accidentally stamped
                `shipping_integration_status="not_required"` by the
                pre-fix payments handler. Dry-run → review → flip. */}
            <MailoutLabelBackfillCard />
            <ExpressSweepCard />

            {/* ── Save toolbar ─────────────────────────────────────── */}
            <div className="sticky bottom-4 z-10">
              <div className="rounded-xl border border-slate-200 bg-white/95 backdrop-blur shadow-lg px-4 py-3 flex items-center justify-between">
                <div className="text-[12px] text-slate-500">
                  {dirty ? (
                    <span className="text-amber-700 font-medium">You have unsaved changes.</span>
                  ) : (
                    <span>All changes saved.</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={load}
                    data-testid="posted-kit-refresh-btn"
                    disabled={saving}
                  >
                    <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={!dirty || saving}
                    data-testid="posted-kit-save-btn"
                    className="bg-cyan-600 hover:bg-cyan-700 text-white"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Saving…
                      </>
                    ) : (
                      <>
                        <Save className="w-3.5 h-3.5 mr-1.5" /> Save changes
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </PortalShell>
  );
}


/**
 * MailoutLabelBackfillCard — Feb 2026.
 *
 * One-click recovery for all-inclusive mail-out bookings that were
 * silently stamped `shipping_integration_status="not_required"` by
 * the pre-fix payments handler. Flow:
 *
 *   1. Preview — POST /admin/fulfilment/backfill-all-inclusive-mail-outs?dry_run=true
 *      Returns the list of affected SCWT references.
 *   2. Confirm — the admin reviews the list.
 *   3. Flip    — same endpoint with `dry_run=false`. Every flip is
 *      appended to `posted_kit_audit_log` on the booking doc.
 */
function MailoutLabelBackfillCard() {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);   // {scanned, references, ...}
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState(null);     // final live-run summary

  const runPreview = async () => {
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post(
        "/admin/fulfilment/backfill-all-inclusive-mail-outs?dry_run=true&limit=500",
      );
      setPreview(data);
      if ((data?.scanned || 0) === 0) {
        toast.info("Nothing to recover — no all-inclusive mail-out bookings are stuck on `not_required`.");
        setConfirming(false);
      } else {
        setConfirming(true);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Preview failed");
    } finally {
      setBusy(false);
    }
  };

  const runFlip = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        "/admin/fulfilment/backfill-all-inclusive-mail-outs?dry_run=false&limit=500",
      );
      setResult(data);
      setConfirming(false);
      setPreview(null);
      toast.success(
        `Recovered ${data?.updated || 0} booking${data?.updated === 1 ? "" : "s"}. They now appear in "Customer kit labels to create".`,
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Backfill failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5" data-testid="mailout-label-backfill-card">
      <h2 className="text-lg font-semibold text-slate-900 mb-1 inline-flex items-center gap-2">
        <RefreshCw className="w-4 h-4 text-slate-500" />
        Recover missing mail-out labels
      </h2>
      <p className="text-[13px] text-slate-600 mb-4 leading-relaxed">
        Fixes all-inclusive mail-out bookings that were accidentally marked
        as <code className="text-[11px] px-1 py-0.5 bg-slate-100 rounded">not_required</code> and
        vanished from the &quot;Customer kit labels to create&quot; count.
        Only touches paid mail-out bookings that have a delivery address,
        no Starshipit order yet, no kit posted, and no sample received. Safe
        to re-run — every flip is logged to the booking&apos;s audit trail.
      </p>

      {!preview && !result && (
        <Button
          onClick={runPreview}
          disabled={busy}
          data-testid="mailout-backfill-preview-btn"
          variant="outline"
          className="border-cyan-300 text-cyan-700 hover:bg-cyan-50"
        >
          {busy ? (
            <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Scanning…</>
          ) : (
            <><Eye className="w-3.5 h-3.5 mr-1.5" /> Preview affected bookings</>
          )}
        </Button>
      )}

      {confirming && preview && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-4">
          <div className="flex items-start gap-2 mb-3">
            <Info className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
            <div className="text-[13px] text-amber-900">
              <div className="font-semibold mb-1">
                {preview.scanned} booking{preview.scanned === 1 ? "" : "s"} will be recovered:
              </div>
              <div className="max-h-32 overflow-y-auto font-mono text-[11px] bg-white/60 rounded p-2 border border-amber-100" data-testid="mailout-backfill-preview-refs">
                {(preview.references || []).join(", ")}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={runFlip}
              disabled={busy}
              data-testid="mailout-backfill-confirm-btn"
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {busy ? (
                <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Recovering…</>
              ) : (
                <>Recover {preview.scanned} booking{preview.scanned === 1 ? "" : "s"}</>
              )}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setPreview(null); setConfirming(false); }}
              disabled={busy}
              data-testid="mailout-backfill-cancel-btn"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {result && (
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4" data-testid="mailout-backfill-result">
          <div className="text-[13px] text-emerald-900 font-semibold mb-2">
            Recovered {result.updated} of {result.scanned}. They now appear in &quot;Customer kit labels to create&quot;.
          </div>
          {(result.references || []).length > 0 && (
            <div className="font-mono text-[11px] bg-white/60 rounded p-2 border border-emerald-100 max-h-32 overflow-y-auto">
              {(result.references || []).join(", ")}
            </div>
          )}
          <div className="mt-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setResult(null)}
              data-testid="mailout-backfill-reset-btn"
            >
              Run another scan
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}


/**
 * ExpressSweepCard — Jun 2026.
 *
 * One-off remediation for bookings paid before the Express-both-ways
 * change: their outbound label is locked to Parcel Post (B30).
 * Preview lists eligible bookings (unshipped, sample not at lab),
 * then a confirmed run voids each old order and creates a fresh
 * Express Post (B20) label.
 */
function ExpressSweepCard() {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);

  const runPreview = async () => {
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/admin/shipping/starshipit/express-sweep?dry_run=true");
      setPreview(data);
      if ((data?.candidates || []).length === 0) {
        toast.info("Nothing to regenerate — every unshipped outbound label is already Express Post.");
        setPreview(null);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Preview failed");
    } finally {
      setBusy(false);
    }
  };

  const runSweep = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/shipping/starshipit/express-sweep?dry_run=false");
      setResult(data);
      setPreview(null);
      toast.success(`Regenerated ${data?.regenerated?.length || 0} outbound label${(data?.regenerated?.length || 0) === 1 ? "" : "s"} as Express Post.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Sweep failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5" data-testid="express-sweep-card">
      <h2 className="text-lg font-semibold text-slate-900 mb-1 inline-flex items-center gap-2">
        <RefreshCw className="w-4 h-4 text-slate-500" />
        Upgrade Parcel Post outbound labels to Express
      </h2>
      <p className="text-[13px] text-slate-600 mb-4 leading-relaxed">
        Bookings paid before the Express-both-ways change still hold a
        Parcel Post (B30) outbound label. This voids each old order and
        creates a fresh Express Post (B20) label. Only touches kits that
        haven&apos;t shipped and whose sample isn&apos;t already at the lab.
        Unused voided labels are never charged by AusPost.
      </p>

      {!preview && !result && (
        <Button
          onClick={runPreview}
          disabled={busy}
          data-testid="express-sweep-preview-btn"
          variant="outline"
          className="border-amber-300 text-amber-700 hover:bg-amber-50"
        >
          {busy ? (
            <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Scanning…</>
          ) : (
            <><Eye className="w-3.5 h-3.5 mr-1.5" /> Preview affected bookings</>
          )}
        </Button>
      )}

      {preview && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-4">
          <div className="text-[13px] text-amber-900 font-semibold mb-1">
            {preview.candidates.length} booking{preview.candidates.length === 1 ? "" : "s"} will get a fresh Express outbound label:
          </div>
          <div className="max-h-32 overflow-y-auto font-mono text-[11px] bg-white/60 rounded p-2 border border-amber-100 mb-2" data-testid="express-sweep-preview-refs">
            {preview.candidates.map((c) => `${c.reference} (${c.service_code})`).join(", ")}
          </div>
          {(preview.skipped || []).length > 0 && (
            <div className="text-[11px] text-amber-800 mb-2">
              Skipped {preview.skipped.length} (sample already at the lab).
            </div>
          )}
          <div className="flex items-center gap-2">
            <Button
              onClick={runSweep}
              disabled={busy}
              data-testid="express-sweep-confirm-btn"
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {busy ? (
                <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Regenerating…</>
              ) : (
                <>Regenerate {preview.candidates.length} label{preview.candidates.length === 1 ? "" : "s"} as Express</>
              )}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setPreview(null)} disabled={busy} data-testid="express-sweep-cancel-btn">
              Cancel
            </Button>
          </div>
        </div>
      )}

      {result && (
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4" data-testid="express-sweep-result">
          <div className="text-[13px] text-emerald-900 font-semibold mb-2">
            Regenerated {(result.regenerated || []).length} of {result.scanned}
            {(result.failed || []).length > 0 && ` — ${result.failed.length} failed`}
            {(result.skipped || []).length > 0 && ` — ${result.skipped.length} skipped`}
          </div>
          {(result.regenerated || []).length > 0 && (
            <div className="font-mono text-[11px] bg-white/60 rounded p-2 border border-emerald-100 max-h-32 overflow-y-auto">
              {result.regenerated.map((r) => `${r.reference} → ${r.new_service_code || "B20"}`).join(", ")}
            </div>
          )}
          {(result.failed || []).length > 0 && (
            <div className="font-mono text-[11px] bg-rose-50 rounded p-2 border border-rose-100 max-h-24 overflow-y-auto mt-2 text-rose-800" data-testid="express-sweep-failed">
              {result.failed.map((r) => `${r.reference}: ${r.reason}`).join(", ")}
            </div>
          )}
          <div className="mt-3">
            <Button variant="ghost" size="sm" onClick={() => setResult(null)} data-testid="express-sweep-reset-btn">
              Run another scan
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
